/**
 * Umrisse der Laubstreu fuer die Lupe der Humusauflage.
 *
 * Nutzer-Wunsch 21.09.2026: „realistischere Blaetter, Ahorn z.B. oder Linde,
 * Ulme, Aspe, Kiefernnadeln ... nicht solche Eier." Jede Art hat hier ihre
 * kennzeichnende Form:
 *  - Ahorn: handfoermig, fuenf spitze Lappen, fuenf Hauptadern vom Stiel aus,
 *  - Linde: herzfoermig mit kurzer Spitze, fein gesaegt, Grund leicht schief,
 *  - Ulme: elliptisch, deutlich schiefer Blattgrund, doppelt gesaegt, viele
 *    gerade, parallele Seitenadern,
 *  - Aspe: fast kreisrund, grob und stumpf gezaehnt, langer Stiel.
 *
 * Einheit: Blattlaenge 1. Der Stiel setzt bei (0, 0) an, die Spitze liegt
 * bei (0, -1). Gezeichnet wird in `auflage-lupe.ts`, das hier ist nur die
 * Form. Reine Funktionen, ohne Zufall - dieselbe Art sieht immer gleich aus;
 * die Vielfalt kommt aus Groesse, Drehung, Spiegelung und Farbe.
 */

export type Blattart = 'ahorn' | 'linde' | 'ulme' | 'aspe';

export const BLATTARTEN: readonly Blattart[] = ['ahorn', 'linde', 'ulme', 'aspe'];

export interface Blattform {
	/** Geschlossener Umriss der Spreite. */
	umriss: string;
	/** Die Adern als offene Linien. */
	adern: string;
	/** Der Stiel als offene Linie. */
	stiel: string;
}

const z = (v: number) => (Math.round(v * 1000) / 1000).toString();

/** Ein Punkt als „x y". */
const p = (x: number, y: number) => `${z(x)} ${z(y)}`;

/* --- Ahorn: feste Eckpunkte ------------------------------------------------ */

/**
 * Die rechte Haelfte des Ahornblattes, vom Stielansatz bis zur Spitze des
 * Mittellappens. Die linke ist ihr Spiegelbild.
 */
const AHORN_RECHTS: [number, number][] = [
	[0, -0.08], [0.12, -0.1], [0.3, -0.04], [0.5, -0.16], [0.4, -0.22], [0.44, -0.29],
	[0.31, -0.33], [0.43, -0.45], [0.58, -0.5], [0.49, -0.57], [0.55, -0.7], [0.4, -0.64],
	[0.36, -0.72], [0.17, -0.55], [0.17, -0.76], [0.27, -0.8], [0.1, -0.85], [0, -1],
];

function ahorn(): Blattform {
	const links = [...AHORN_RECHTS].reverse().slice(1).map(([x, y]) => [-x, y] as [number, number]);
	const alle = [...AHORN_RECHTS, ...links];
	const umriss = alle.map(([x, y], k) => (k ? 'L' : 'M') + p(x, y)).join('') + 'Z';
	const ansatz = p(0, -0.08);
	const adern = [[0, -0.96], [0.52, -0.68], [-0.52, -0.68], [0.47, -0.17], [-0.47, -0.17]]
		.map(([x, y]) => `M${ansatz}L${p(x, y)}`).join('');
	return { umriss, adern, stiel: `M${ansatz}L${p(0.02, 0.38)}` };
}

/* --- Linde, Ulme, Aspe: Umriss um einen Mittelpunkt ------------------------ */

/**
 * Ein Umriss, beschrieben durch den Abstand vom Mittelpunkt je Richtung.
 * Die Richtung zaehlt in Grad von der Spitze (0) ueber die rechte Seite bis
 * zum Stielansatz (180); zwischen den Stuetzstellen wird geradlinig
 * gemittelt.
 */
interface PolarForm {
	mitteY: number;
	/** Stuetzstellen der rechten Seite: [Grad, Abstand]. */
	rechts: [number, number][];
	/** Wie viel kleiner die linke Seite je Richtung ist (schiefer Blattgrund). */
	links: (grad: number) => number;
	/** Schrittweite in Grad; jeder Schritt ist ein Zahn oder eine Bucht. */
	schritt: number;
	/** Zahnmuster, wird der Reihe nach wiederholt (Anteil am Abstand). */
	zaehne: number[];
}

function abstandBei(stuetzen: [number, number][], grad: number): number {
	for (let i = 1; i < stuetzen.length; i++) {
		const [g1, r1] = stuetzen[i - 1], [g2, r2] = stuetzen[i];
		if (grad <= g2) return r1 + (r2 - r1) * ((grad - g1) / (g2 - g1));
	}
	return stuetzen[stuetzen.length - 1][1];
}

/** Ein Randpunkt, ohne Zaehne; fuer die Enden der Adern. */
function randpunkt(f: PolarForm, grad: number, seite: 1 | -1): [number, number] {
	const r = abstandBei(f.rechts, grad) * (seite < 0 ? f.links(grad) : 1);
	const w = (grad * Math.PI) / 180;
	return [seite * r * Math.sin(w), f.mitteY - r * Math.cos(w)];
}

function polarUmriss(f: PolarForm): string {
	const punkte: string[] = [];
	let k = 0;
	const schritte = Math.round(180 / f.schritt);
	// Rechts von der Spitze zum Stiel, dann links zurueck zur Spitze.
	for (const seite of [1, -1] as const) {
		for (let i = 0; i <= schritte; i++) {
			const grad = seite > 0 ? i * f.schritt : 180 - i * f.schritt;
			if (seite < 0 && (i === 0 || i === schritte)) continue;
			// An Spitze und Stielansatz keine Zaehne, sonst franst die Spitze aus.
			const zahn = grad < 8 || grad > 172 ? 0 : f.zaehne[k++ % f.zaehne.length];
			// Ein Zahn schiebt den Randpunkt vom Mittelpunkt weg, eine Bucht zu ihm hin.
			const [x, y] = randpunkt(f, grad, seite);
			punkte.push(p(x * (1 + zahn), f.mitteY + (y - f.mitteY) * (1 + zahn)));
		}
	}
	return punkte.map((q, i) => (i ? 'L' : 'M') + q).join('') + 'Z';
}

/**
 * Fiedernervige Seitenadern: je eine vom Mittelnerv schraeg nach oben an den
 * Rand, auf beiden Seiten. `richtungen` sind die Randstellen in Grad.
 */
function seitenadern(f: PolarForm, richtungen: number[], steigung: number): string {
	let d = '';
	for (const grad of richtungen) {
		for (const seite of [1, -1] as const) {
			const [x, y] = randpunkt(f, grad, seite);
			d += `M${p(0, y + Math.abs(x) * steigung)}L${p(x * 0.93, y)}`;
		}
	}
	return d;
}

const LINDE: PolarForm = {
	mitteY: -0.52,
	rechts: [[0, 0.48], [6, 0.36], [20, 0.33], [50, 0.37], [90, 0.41], [120, 0.43],
		[145, 0.46], [160, 0.5], [170, 0.5], [176, 0.47], [180, 0.44]],
	links: g => (g > 90 ? 0.94 : 1),
	schritt: 4,
	zaehne: [0.035, -0.005],
};

function linde(): Blattform {
	const ansatz = p(0, -0.08);
	const [bx, by] = randpunkt(LINDE, 130, 1);
	const [cx, cy] = randpunkt(LINDE, 130, -1);
	const adern = `M${ansatz}L${p(0, -0.97)}`
		+ `M${ansatz}L${p(bx * 0.9, by)}M${ansatz}L${p(cx * 0.9, cy)}`
		+ seitenadern(LINDE, [30, 55, 80], 0.45);
	return { umriss: polarUmriss(LINDE), adern, stiel: `M${ansatz}L${p(0.03, 0.26)}` };
}

const ULME: PolarForm = {
	mitteY: -0.5,
	rechts: [[0, 0.5], [7, 0.36], [20, 0.3], [50, 0.29], [90, 0.27], [130, 0.29],
		[160, 0.37], [175, 0.44], [180, 0.46]],
	// Der schiefe Blattgrund: links setzt die Spreite hoeher an.
	links: g => (g > 120 ? 1 - (g - 120) / 60 * 0.22 : 1),
	schritt: 4,
	// Doppelt gesaegt: ein grosser Zahn, dazwischen ein kleiner.
	zaehne: [0.06, 0, 0.025, 0],
};

function ulme(): Blattform {
	const ansatz = p(0, -0.04);
	const richtungen = [22, 34, 46, 58, 70, 82, 94, 106, 118, 130, 142, 154];
	const adern = `M${ansatz}L${p(0, -0.97)}` + seitenadern(ULME, richtungen, 0.55);
	return { umriss: polarUmriss(ULME), adern, stiel: `M${ansatz}L${p(0, 0.1)}` };
}

const ASPE: PolarForm = {
	mitteY: -0.48,
	rechts: [[0, 0.52], [10, 0.45], [30, 0.46], [60, 0.47], [90, 0.47], [120, 0.46],
		[150, 0.44], [170, 0.46], [180, 0.44]],
	links: () => 1,
	schritt: 9,
	// Grob und stumpf gezaehnt.
	zaehne: [0.05, -0.02],
};

function aspe(): Blattform {
	const ansatz = p(0, -0.04);
	const [bx, by] = randpunkt(ASPE, 110, 1);
	const [cx, cy] = randpunkt(ASPE, 110, -1);
	const adern = `M${ansatz}L${p(0, -0.96)}`
		+ `M${ansatz}L${p(bx * 0.85, by)}M${ansatz}L${p(cx * 0.85, cy)}`
		+ seitenadern(ASPE, [35, 65], 0.4);
	return { umriss: polarUmriss(ASPE), adern, stiel: `M${ansatz}L${p(-0.02, 0.46)}` };
}

const FORMEN: Record<Blattart, Blattform> = {
	ahorn: ahorn(), linde: linde(), ulme: ulme(), aspe: aspe(),
};

/** Die Form einer Blattart. */
export function blattform(art: Blattart): Blattform {
	return FORMEN[art];
}
